/**
 * Logique pure de l'éditeur d'articles de facture (clone du bloc « Produits et
 * services » de Qonto). Les champs sont des chaînes (saisies de formulaire) ;
 * la virgule et le point sont acceptés comme séparateur décimal.
 *
 * Le prix unitaire HT est la source de vérité : le TTC affiché en est dérivé.
 */

export type DiscountType = 'percentage' | 'absolute';

export interface InvoiceItemDiscountDraft {
  type: DiscountType;
  /** Pourcentage saisi tel quel ('10' = 10 %) ou montant en euros ('5,00'). */
  value: string;
}

export interface InvoiceItemDraft {
  key: string;
  title: string;
  description: string;
  quantity: string;
  unit: string;
  unitPriceHT: string;
  /** '0', '2.1', '5.5', '8.5', '10', '20' ou un taux personnalisé ('7'). */
  vatRate: string;
  discount?: InvoiceItemDiscountDraft | null;
}

export const VAT_RATES = ['0', '2.1', '5.5', '8.5', '10', '20'] as const;
export const DEFAULT_VAT_RATE = '20';

export const TITLE_MAX_LENGTH = 120;
export const DESCRIPTION_MAX_LENGTH = 1800;
export const UNIT_MAX_LENGTH = 20;

let keyCounter = 0;
const nextKey = (): string => {
  keyCounter += 1;
  return `item-${Date.now().toString(36)}-${keyCounter}-${Math.random().toString(36).slice(2, 7)}`;
};

export const newItem = (): InvoiceItemDraft => ({
  key: nextKey(),
  title: '',
  description: '',
  quantity: '1',
  unit: '',
  unitPriceHT: '',
  vatRate: DEFAULT_VAT_RATE,
  discount: null,
});

/** Arrondi au centime, robuste aux erreurs flottantes (1.005 → 1.01). */
export const round2 = (n: number): number => {
  const r = Math.round(Math.abs(n) * 100 + 1e-7) / 100;
  return n < 0 ? -r : r;
};

/** '12,5' / '12.50' / ' 1 200,00 ' → nombre ; vide ou invalide → null. */
export const parseDecimal = (s: string | null | undefined): number | null => {
  if (s === null || s === undefined) return null;
  const cleaned = String(s)
    .replace(/[\s  ]/g, '')
    .replace(',', '.');
  if (cleaned === '' || !/^-?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const rateOf = (vatRate: string): number => parseDecimal(vatRate) ?? 0;

export const ttcFromHt = (ht: number, vatRate: string): number => round2(ht * (1 + rateOf(vatRate) / 100));

export const htFromTtc = (ttc: number, vatRate: string): number => round2(ttc / (1 + rateOf(vatRate) / 100));

export interface LineTotals {
  ht: number;
  discountAmount: number;
  htAfterDiscount: number;
  vat: number;
  ttc: number;
}

export const lineTotals = (item: InvoiceItemDraft): LineTotals => {
  const quantity = parseDecimal(item.quantity) ?? 0;
  const unitPrice = parseDecimal(item.unitPriceHT) ?? 0;
  const ht = round2(quantity * unitPrice);

  let discountAmount = 0;
  if (item.discount) {
    const value = parseDecimal(item.discount.value) ?? 0;
    discountAmount = item.discount.type === 'percentage' ? round2((ht * value) / 100) : round2(value);
  }
  const htAfterDiscount = round2(ht - discountAmount);
  const vat = round2((htAfterDiscount * rateOf(item.vatRate)) / 100);
  return { ht, discountAmount, htAfterDiscount, vat, ttc: round2(htAfterDiscount + vat) };
};

/** Taux normalisé pour l'affichage et le regroupement ('5,50' → '5.5'). */
export const normalizeRate = (vatRate: string): string => String(rateOf(vatRate));

export interface VatByRate {
  rate: string;
  base: number;
  amount: number;
}

export interface InvoiceTotals {
  totalHT: number;
  vatByRate: VatByRate[];
  totalVAT: number;
  totalTTC: number;
}

export const invoiceTotals = (items: InvoiceItemDraft[]): InvoiceTotals => {
  const byRate = new Map<string, VatByRate>();
  let totalHT = 0;
  let totalVAT = 0;
  for (const item of items) {
    const t = lineTotals(item);
    const rate = normalizeRate(item.vatRate);
    const entry = byRate.get(rate) ?? { rate, base: 0, amount: 0 };
    entry.base = round2(entry.base + t.htAfterDiscount);
    entry.amount = round2(entry.amount + t.vat);
    byRate.set(rate, entry);
    totalHT = round2(totalHT + t.htAfterDiscount);
    totalVAT = round2(totalVAT + t.vat);
  }
  const vatByRate = [...byRate.values()].sort((a, b) => Number(a.rate) - Number(b.rate));
  return { totalHT, vatByRate, totalVAT, totalTTC: round2(totalHT + totalVAT) };
};

export type InvoiceItemField = 'title' | 'description' | 'unit' | 'quantity' | 'unitPriceHT' | 'vatRate' | 'discount';
export type InvoiceItemErrors = Partial<Record<InvoiceItemField, string>>;

export interface InvoiceItemsValidation {
  valid: boolean;
  /** Erreur de niveau liste (aucun article). */
  global: string | null;
  /** Une entrée par article, dans l'ordre ; objet vide si l'article est valide. */
  items: InvoiceItemErrors[];
}

export const validateItem = (item: InvoiceItemDraft): InvoiceItemErrors => {
  const errors: InvoiceItemErrors = {};
  const title = item.title.trim();
  if (!title) errors.title = 'Le titre est obligatoire.';
  else if (title.length > TITLE_MAX_LENGTH)
    errors.title = `Le titre ne doit pas dépasser ${TITLE_MAX_LENGTH} caractères.`;

  if (item.description.length > DESCRIPTION_MAX_LENGTH)
    errors.description = `Les détails ne doivent pas dépasser ${DESCRIPTION_MAX_LENGTH} caractères.`;

  if (item.unit.trim().length > UNIT_MAX_LENGTH)
    errors.unit = `L'unité ne doit pas dépasser ${UNIT_MAX_LENGTH} caractères.`;

  const quantity = parseDecimal(item.quantity);
  if (quantity === null) errors.quantity = 'Indiquez une quantité valide.';
  else if (quantity <= 0) errors.quantity = 'La quantité doit être supérieure à 0.';

  const unitPrice = parseDecimal(item.unitPriceHT);
  if (unitPrice === null) errors.unitPriceHT = 'Indiquez un prix unitaire valide.';
  else if (unitPrice < 0) errors.unitPriceHT = 'Le prix unitaire ne peut pas être négatif.';

  const rate = parseDecimal(item.vatRate);
  if (rate === null || rate < 0 || rate > 100) errors.vatRate = 'Indiquez un taux de TVA entre 0 et 100 %.';

  if (item.discount) {
    const value = parseDecimal(item.discount.value);
    if (value === null || value < 0) {
      errors.discount = 'Indiquez une remise valide.';
    } else if (item.discount.type === 'percentage' && value > 100) {
      errors.discount = 'La remise doit être comprise entre 0 et 100 %.';
    } else if (item.discount.type === 'absolute' && quantity !== null && unitPrice !== null) {
      if (round2(value) > round2(quantity * unitPrice))
        errors.discount = "La remise ne peut pas dépasser le montant de l'article.";
    }
  }
  return errors;
};

export const validateItems = (items: InvoiceItemDraft[]): InvoiceItemsValidation => {
  const itemErrors = items.map(validateItem);
  const global = items.length === 0 ? 'Ajoutez au moins un article.' : null;
  const valid = global === null && itemErrors.every((e) => Object.keys(e).length === 0);
  return { valid, global, items: itemErrors };
};

/** Format attendu par Qonto POST /v2/client_invoices (items[]). */
export interface QontoInvoiceItem {
  title: string;
  description?: string;
  quantity: string;
  unit?: string;
  unit_price: { value: string; currency: 'EUR' };
  vat_rate: string;
  discount?: { type: DiscountType; value: string };
}

/** 20 → '0.2', 5.5 → '0.055', 0 → '0' (sans bruit flottant). */
const percentToFraction = (percent: number): string => String(Number((percent / 100).toFixed(6)));

/** À n'appeler que sur des articles validés (validateItems). */
export const toQontoItems = (items: InvoiceItemDraft[]): QontoInvoiceItem[] =>
  items.map((item) => {
    const out: QontoInvoiceItem = {
      title: item.title.trim(),
      quantity: String(parseDecimal(item.quantity) ?? 0),
      unit_price: { value: (parseDecimal(item.unitPriceHT) ?? 0).toFixed(2), currency: 'EUR' },
      vat_rate: percentToFraction(rateOf(item.vatRate)),
    };
    const description = item.description.trim();
    if (description) out.description = description;
    const unit = item.unit.trim();
    if (unit) out.unit = unit;
    if (item.discount) {
      const value = parseDecimal(item.discount.value);
      if (value !== null && value > 0) {
        out.discount = {
          type: item.discount.type,
          value: item.discount.type === 'percentage' ? percentToFraction(value) : value.toFixed(2),
        };
      }
    }
    return out;
  });

/** '20' → '20 %', '5.5' → '5,5 %'. */
export const formatRate = (vatRate: string): string => `${normalizeRate(vatRate).replace('.', ',')} %`;

const eurFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
export const formatEur = (n: number): string => eurFormatter.format(n);

/** Nombre → saisie à la française ('12,50'). */
export const toInputString = (n: number): string => n.toFixed(2).replace('.', ',');
